import {
  Alert,
  Button,
  Card,
  Col,
  Empty,
  Input,
  Row,
  Space,
  Spin,
  Statistic,
  Table,
  Tabs,
  Tag,
  Typography,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ReloadOutlined, SaveOutlined } from '@ant-design/icons';
import { useEffect, useMemo, useState } from 'react';
import AdminLayout from '../components/AdminLayout';
import { cukcukMappingService, type CukcukMaterialMapping, type CukcukStoreMapping } from '../services/cukcukMappingService';
import { dataEntryUserService } from '../services/dataEntryUserService';
import type { DataEntryUser } from '../types/dataEntryUser';

const getCurrentUserId = (): string => {
  for (const key of ['dataEntryUserId', 'userId', 'localId', 'zaloUserId']) {
    const value = localStorage.getItem(key)?.trim();
    if (value) return value;
  }
  return 'admin8amcoffee';
};

const statusTag = (status: 'mapped' | 'unmapped') => (
  <Tag color={status === 'mapped' ? 'success' : 'warning'}>
    {status === 'mapped' ? 'Đã mapping' : 'Chưa mapping'}
  </Tag>
);

const MappingSummary = ({
  total,
  mapped,
  unmapped,
  title,
}: {
  total: number;
  mapped: number;
  unmapped: number;
  title: string;
}) => (
  <Card size="small" title={title} style={{ height: '100%' }}>
    <Row gutter={12}>
      <Col span={8}><Statistic title="Tổng" value={total} /></Col>
      <Col span={8}><Statistic title="Đã mapping" value={mapped} valueStyle={{ color: '#1677ff' }} /></Col>
      <Col span={8}><Statistic title="Chưa mapping" value={unmapped} valueStyle={{ color: '#d89614' }} /></Col>
    </Row>
  </Card>
);

const CukcukMappingManagementPage = () => {
  const [materials, setMaterials] = useState<CukcukMaterialMapping[]>([]);
  const [stores, setStores] = useState<CukcukStoreMapping[]>([]);
  const [summary, setSummary] = useState({
    materials: { total: 0, mapped: 0, unmapped: 0 },
    stores: { total: 0, mapped: 0, unmapped: 0 },
  });
  const [currentUser, setCurrentUser] = useState<DataEntryUser | null>(null);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [materialDrafts, setMaterialDrafts] = useState<Record<string, { code: string; unit: string }>>({});
  const [storeDrafts, setStoreDrafts] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [messageApi, contextHolder] = message.useMessage();

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [materialRows, storeRows, mappingSummary] = await Promise.all([
        cukcukMappingService.getMaterialMappings(),
        cukcukMappingService.getStoreMappings(),
        cukcukMappingService.getSummary(),
      ]);
      setMaterials(materialRows);
      setStores(storeRows);
      setSummary(mappingSummary);
      setMaterialDrafts(Object.fromEntries(materialRows.map((item) => [
        item.id,
        { code: item.cukcukMaterialCode || '', unit: item.cukcukUnit || '' },
      ])));
      setStoreDrafts(Object.fromEntries(storeRows.map((item) => [item.id, item.cukcukWarehouseCode || ''])));
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'Không thể tải cấu hình CUKCUK');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let active = true;
    dataEntryUserService.getUserByUserId(getCurrentUserId())
      .then((user) => {
        if (active) setCurrentUser(user || {
          userId: getCurrentUserId(),
          name: 'Admin 8AM',
          avatar: '',
          isAdmin: true,
          dataEntryRole: true,
          createdAt: new Date(),
          updatedAt: new Date(),
        });
      })
      .catch(() => {
        if (active) setCurrentUser(null);
      });
    load();
    return () => { active = false; };
  }, []);

  const actor = { isAdmin: Boolean(currentUser?.isAdmin) };
  const canEdit = actor.isAdmin;
  const normalizedSearch = search.trim().toLocaleLowerCase('vi');

  const filteredMaterials = useMemo(
    () => materials.filter((item) =>
      !normalizedSearch || `${item.name} ${item.internalCode} ${item.cukcukMaterialCode || ''}`
        .toLocaleLowerCase('vi').includes(normalizedSearch),
    ),
    [materials, normalizedSearch],
  );
  const filteredStores = useMemo(
    () => stores.filter((item) =>
      !normalizedSearch || `${item.name} ${item.cukcukWarehouseCode || ''}`
        .toLocaleLowerCase('vi').includes(normalizedSearch),
    ),
    [stores, normalizedSearch],
  );

  const saveMaterial = async (record: CukcukMaterialMapping) => {
    const draft = materialDrafts[record.id] || { code: '', unit: '' };
    setSavingKey(`material:${record.id}`);
    try {
      await cukcukMappingService.updateMaterialMapping(
        record.id,
        { cukcukMaterialCode: draft.code, cukcukUnit: draft.unit },
        actor,
      );
      messageApi.success('Đã lưu mapping nguyên vật liệu');
      await load();
    } catch (saveError) {
      messageApi.error(saveError instanceof Error ? saveError.message : 'Không thể lưu mapping');
    } finally {
      setSavingKey(null);
    }
  };

  const saveStore = async (record: CukcukStoreMapping) => {
    const code = storeDrafts[record.id] || '';
    setSavingKey(`store:${record.id}`);
    try {
      await cukcukMappingService.updateStoreMapping(record.id, { cukcukWarehouseCode: code }, actor);
      messageApi.success('Đã lưu mapping kho CUKCUK');
      await load();
    } catch (saveError) {
      messageApi.error(saveError instanceof Error ? saveError.message : 'Không thể lưu mapping');
    } finally {
      setSavingKey(null);
    }
  };

  const materialColumns: ColumnsType<CukcukMaterialMapping> = [
    { title: 'Nguyên vật liệu', dataIndex: 'name', key: 'name' },
    { title: 'Mã nội bộ', dataIndex: 'internalCode', key: 'internalCode', width: 150 },
    {
      title: 'Mã NVL CUKCUK', key: 'cukcukMaterialCode', width: 220,
      render: (_, record) => (
        <Input
          value={materialDrafts[record.id]?.code ?? record.cukcukMaterialCode ?? ''}
          disabled={!canEdit}
          data-testid={`material-code-${record.id}`}
          onChange={(event) => setMaterialDrafts((current) => ({
            ...current,
            [record.id]: { ...(current[record.id] || { unit: record.cukcukUnit || '' }), code: event.target.value },
          }))}
        />
      ),
    },
    {
      title: 'ĐVT CUKCUK', key: 'cukcukUnit', width: 180,
      render: (_, record) => (
        <Input
          value={materialDrafts[record.id]?.unit ?? record.cukcukUnit ?? ''}
          disabled={!canEdit}
          data-testid={`material-unit-${record.id}`}
          onChange={(event) => setMaterialDrafts((current) => ({
            ...current,
            [record.id]: { ...(current[record.id] || { code: record.cukcukMaterialCode || '' }), unit: event.target.value },
          }))}
        />
      ),
    },
    { title: 'Trạng thái', key: 'status', render: (_, record) => statusTag(record.status), width: 140 },
    {
      title: 'Thao tác', key: 'actions', width: 110,
      render: (_, record) => (
        <Button
          icon={<SaveOutlined />}
          data-testid={`save-material-${record.id}`}
          type="primary"
          disabled={!canEdit}
          loading={savingKey === `material:${record.id}`}
          onClick={() => { void saveMaterial(record); }}
        >Lưu</Button>
      ),
    },
  ];

  const storeColumns: ColumnsType<CukcukStoreMapping> = [
    { title: 'Cơ sở', dataIndex: 'name', key: 'name' },
    {
      title: 'Mã kho CUKCUK', key: 'cukcukWarehouseCode', width: 260,
      render: (_, record) => (
        <Input
          value={storeDrafts[record.id] ?? record.cukcukWarehouseCode ?? ''}
          disabled={!canEdit}
          data-testid={`store-code-${record.id}`}
          onChange={(event) => setStoreDrafts((current) => ({ ...current, [record.id]: event.target.value }))}
        />
      ),
    },
    { title: 'Trạng thái', key: 'status', render: (_, record) => statusTag(record.status), width: 140 },
    {
      title: 'Thao tác', key: 'actions', width: 110,
      render: (_, record) => (
        <Button
          icon={<SaveOutlined />}
          data-testid={`save-store-${record.id}`}
          type="primary"
          disabled={!canEdit}
          loading={savingKey === `store:${record.id}`}
          onClick={() => { void saveStore(record); }}
        >Lưu</Button>
      ),
    },
  ];

  return (
    <AdminLayout>
      {contextHolder}
      <div style={{ padding: '24px 0' }}>
        <Space direction="vertical" size={20} style={{ width: '100%' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
            <div>
              <Typography.Title level={2} style={{ margin: 0 }}>Cấu hình CUKCUK</Typography.Title>
              <Typography.Text type="secondary">Mapping mã nguyên vật liệu và mã kho trước khi xuất nhập hàng.</Typography.Text>
            </div>
            <Button icon={<ReloadOutlined />} onClick={() => void load()} loading={loading}>Tải lại</Button>
          </div>

          {error && <Alert type="error" showIcon message={error} />}
          {!canEdit && currentUser && (
            <Alert type="info" showIcon message="Bạn chỉ có quyền xem. Chỉ Admin được sửa mapping CUKCUK." />
          )}

          <Row gutter={[16, 16]}>
            <Col xs={24} md={12}><MappingSummary title="Nguyên vật liệu" {...summary.materials} /></Col>
            <Col xs={24} md={12}><MappingSummary title="Cơ sở / kho" {...summary.stores} /></Col>
          </Row>

          <Card>
            <Input.Search
              allowClear
              placeholder="Tìm theo tên, mã nội bộ hoặc mã CUKCUK"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              style={{ maxWidth: 460, marginBottom: 16 }}
            />
            {loading ? <Spin /> : (
              <Tabs items={[
                {
                  key: 'materials', label: `Nguyên vật liệu (${filteredMaterials.length})`,
                  children: filteredMaterials.length ? (
                    <Table rowKey="id" columns={materialColumns} dataSource={filteredMaterials} scroll={{ x: 850 }} pagination={{ pageSize: 10 }} />
                  ) : <Empty description="Không có nguyên vật liệu" />,
                },
                {
                  key: 'stores', label: `Cơ sở / kho (${filteredStores.length})`,
                  children: filteredStores.length ? (
                    <Table rowKey="id" columns={storeColumns} dataSource={filteredStores} scroll={{ x: 700 }} pagination={{ pageSize: 10 }} />
                  ) : <Empty description="Không có cơ sở" />,
                },
              ]} />
            )}
          </Card>
        </Space>
      </div>
    </AdminLayout>
  );
};

export default CukcukMappingManagementPage;
